'use client';

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createReferralShareData, copyToClipboard } from '@/utils/share';
import { AttributionSource, AttributionMedium } from '@/constants/attribution';
import { ShareButtons } from '@/components/common/ShareButtons';
import { Copy, Check } from 'lucide-react';
import { getReferralShareMessage } from '@/utils/share';

interface ReferJobModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle?: string;
  candidateAppBaseUrl: string;
}

export function ReferJobModal({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  candidateAppBaseUrl,
}: ReferJobModalProps) {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'share'>('email');
  const [copied, setCopied] = useState(false);

  const referralUrl =
    step === 'share' && email.trim()
      ? `${candidateAppBaseUrl.replace(/\/$/, '')}/?jobId=${jobId}&source=${AttributionSource.REFERRAL}&medium=${AttributionMedium.JOB_PAGE}&campaign=${encodeURIComponent(email.trim())}`
      : '';

  const shareData = referralUrl ? createReferralShareData(referralUrl) : null;

  const handleContinue = useCallback(() => {
    if (!email.trim()) return;
    setStep('share');
  }, [email]);

  const handleCopyLink = useCallback(async () => {
    if (!referralUrl) return;
    const success = await copyToClipboard(referralUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [referralUrl]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setStep('email');
        setEmail('');
        setCopied(false);
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        {step === 'email' ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Get Your Referral Link</DialogTitle>
              <DialogDescription className="text-base pt-1">
                Enter your email to generate your unique referral link and share it.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-5 pt-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="referrer-email" className="font-bold text-sm">Your Email Address</Label>
                <Input
                  id="referrer-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
                  className="h-11"
                />
              </div>

              <Button
                onClick={handleContinue}
                disabled={!email.trim()}
                size="lg"
                className="w-full font-medium"
              >
                Continue
              </Button>

              <div className="bg-muted/50 rounded-lg p-5 space-y-3">
                <h4 className="font-bold text-sm">How it works:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span>✓</span> Share your unique link with your network
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> They apply using your link
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> The application is attributed to you
                  </li>
                </ul>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Share Your Referral Link</DialogTitle>
              <DialogDescription className="text-base pt-1">
                Copy your link or share directly to your network.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-5 pt-4">
              <div className="border border-border/60 rounded-xl p-4 space-y-3">
                <Label className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Your Referral Link</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted/40 rounded-lg p-3 font-mono text-sm break-all font-medium border border-border/40">
                    {referralUrl}
                  </div>
                  <Button
                    variant="outline"
                    className="h-[46px] w-[46px] shrink-0 rounded-lg group"
                    onClick={handleCopyLink}
                  >
                    {copied ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                    )}
                  </Button>
                </div>
              </div>

              {shareData && (
                <div className="space-y-3">
                  <h4 className="font-bold text-[15px]">Quick Share</h4>
                  <ShareButtons
                    data={shareData}
                    platforms={['whatsapp', 'linkedin', 'x']}
                  />
                </div>
              )}

              <Button
                onClick={() => handleOpenChange(false)}
                size="lg"
                className="w-full font-medium h-12 rounded-xl text-base mt-2"
              >
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
